import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import {
	MAX_CUTOFF_HOURS,
	splitCutoffHours,
	toCutoffHours,
} from "../../utils/acceptance-cutoff";

interface CutoffHoursInputProps {
	/** Decimal hours, e.g. 2.5 for 02:30. */
	value: number;
	onChange: (hours: number) => void;
	disabled?: boolean;
	hoursUnitLabel?: string;
	minutesUnitLabel?: string;
}

const filledInputStyles = {
	"& .MuiInputBase-input": {
		background: "#FFF",
		padding: "8px",
		textAlign: "center" as const,
	},
};

/**
 * Sets an acceptance cutoff as hh:mm. Hours are typed (0 … 168) and minutes are
 * picked from 00 / 30, since the cutoff only moves in half-hour steps. The
 * value in and out is always decimal hours, matching the wire format.
 */
function CutoffHoursInput({
	value,
	onChange,
	disabled = false,
	hoursUnitLabel = "hrs",
	minutesUnitLabel = "min",
}: CutoffHoursInputProps) {
	const { hours, minutes } = splitCutoffHours(value);

	// The hours field is held as text so it can be empty mid-edit rather than
	// snapping back to 0 on every keystroke.
	const [hoursText, setHoursText] = useState(String(hours));

	useEffect(() => {
		setHoursText(String(hours));
	}, [hours]);

	const handleHoursChange = (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const digitsOnly = event.target.value.replace(/[^0-9]/g, "");
		const capped =
			digitsOnly === ""
				? digitsOnly
				: String(Math.min(Number(digitsOnly), MAX_CUTOFF_HOURS));
		setHoursText(capped);
		if (capped !== "") {
			onChange(toCutoffHours(Number(capped), minutes));
		}
	};

	// Committing an empty field falls back to the clamped minimum.
	const handleHoursBlur = () => {
		if (hoursText === "") {
			const restored = toCutoffHours(0, minutes);
			onChange(restored);
			setHoursText(String(splitCutoffHours(restored).hours));
		}
	};

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
			<TextField
				id="cutoff-hours"
				variant="filled"
				size="small"
				disabled={disabled}
				value={hoursText}
				onChange={handleHoursChange}
				onBlur={handleHoursBlur}
				inputProps={{
					inputMode: "numeric",
					"aria-label": hoursUnitLabel,
				}}
				sx={{ ...filledInputStyles, width: "72px" }}
			/>
			<Typography
				sx={{
					fontFamily: "Roboto",
					fontSize: "14px",
					color: "rgba(0, 0, 0, 0.6)",
				}}
			>
				{hoursUnitLabel}
			</Typography>
			<Select
				id="cutoff-minutes"
				variant="filled"
				disabled={disabled}
				value={minutes}
				onChange={(event) =>
					onChange(toCutoffHours(Number(hoursText || 0), Number(event.target.value)))
				}
				sx={{ ...filledInputStyles, width: "72px" }}
			>
				<MenuItem value={0}>00</MenuItem>
				<MenuItem value={30}>30</MenuItem>
			</Select>
			<Typography
				sx={{
					fontFamily: "Roboto",
					fontSize: "14px",
					color: "rgba(0, 0, 0, 0.6)",
				}}
			>
				{minutesUnitLabel}
			</Typography>
		</Box>
	);
}

export default CutoffHoursInput;
